import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type {
  AmountDifferenceType,
  AssessmentsAccumulationMappingResponse,
} from '@/entities/assessments/api/generated/model';
import { getAmountDifferenceTypeLabel } from '@/entities/assessments/lib/getAmountDifferenceTypeLabel';
import type { AmountUnit } from '@/shared/lib/amount/convertAmount';
import { convertAmount } from '@/shared/lib/amount/convertAmount';
import { formatAmount } from '@/shared/lib/format';
import { Status, type StatusVariant, Text } from '@/shared/ui';

type AIAccumulationsTableColumnsProps = {
  /** 基準年度 */
  baseFiscalYear: number;
  /** 比較年度 */
  comparedFiscalYear: number;
  /** 金額単位 */
  amountUnit: AmountUnit;
  /** 文言の翻訳 */
  t: TFunction<'translation'>;
};

export const STATUS_COLUMN_WIDTH = 95;
const AMOUNT_COLUMN_MIN_WIDTH = 200;

/** 状態に応じたStatusバリアントを返す */
const getStatusVariant = (type: AmountDifferenceType): StatusVariant => {
  switch (type) {
    case 'increased':
    case 'new':
      return 'error';
    case 'decreased':
    case 'deleted':
      return 'success';
    default:
      return 'neutral';
  }
};

/** 状態に応じた差額の色を返す */
const getDifferenceColor = (type: AmountDifferenceType): 'base' | 'success' | 'error' => {
  switch (type) {
    case 'increased':
    case 'new':
      return 'error';
    case 'decreased':
    case 'deleted':
      return 'success';
    default:
      return 'base';
  }
};

/** 差額の表示テキストを生成 */
const formatDifferenceAmount = ({
  amount,
  type,
  amountUnit,
  t,
}: {
  amount: number;
  type: AmountDifferenceType;
  amountUnit: AmountUnit;
  t: TFunction<'translation'>;
}): string => {
  const convertedAmount = convertAmount(Math.abs(amount), amountUnit) ?? 0;
  const unitKey =
    amountUnit === 'thousand' ? 'common.unit.amount-thousand-yen' : 'common.unit.amount-yen';
  const formattedAmount = t(unitKey, { amount: formatAmount(convertedAmount) });
  if (type === 'unchanged') {
    return formattedAmount;
  }
  if (type === 'increased' || type === 'new') {
    return `+ ${formattedAmount}`;
  }
  return `- ${formattedAmount}`;
};

/**
 * AI積算明細テーブルのカラム定義を生成
 */
export const createAIAccumulationsTableColumns = ({
  baseFiscalYear,
  comparedFiscalYear,
  amountUnit,
  t,
}: AIAccumulationsTableColumnsProps): ColumnDef<AssessmentsAccumulationMappingResponse>[] => {
  return [
    {
      id: 'status',
      header: t('assessment.comparison-table.fields.status'),
      meta: { width: STATUS_COLUMN_WIDTH },
      cell: ({ row }) => {
        const type = row.original.amount_difference_type;
        return (
          <Status variant={getStatusVariant(type)} className="font-medium text-xs">
            {getAmountDifferenceTypeLabel({ type, t })}
          </Status>
        );
      },
    },
    {
      id: 'base_amount',
      header: () => (
        <div className="flex flex-col gap-0.5">
          <Text as="span" variant="label" color="muted">
            {t('assessment.comparison-table.summary')}
          </Text>
          <Text as="span" variant="label" color="muted">
            {t('assessment.comparison-table.budget-amount', { year: baseFiscalYear })}
          </Text>
        </div>
      ),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        return (
          <div className="flex w-full flex-col gap-0.5">
            <Text>{row.original.base_description}</Text>
            <Text viewMode="number" unit={amountUnit} className="block w-full text-right">
              {row.original.base_total_amount}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'compared_amount',
      header: () => (
        <div className="flex flex-col gap-0.5">
          <Text as="span" variant="label" color="muted">
            {t('assessment.comparison-table.summary')}
          </Text>
          <Text as="span" variant="label" color="muted">
            {t('assessment.comparison-table.budget-amount', { year: comparedFiscalYear })}
          </Text>
        </div>
      ),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        return (
          <div className="flex w-full flex-col gap-0.5">
            <Text>{row.original.compared_description}</Text>
            <Text viewMode="number" unit={amountUnit} className="block w-full text-right">
              {row.original.compared_total_amount}
            </Text>
          </div>
        );
      },
    },
    {
      id: 'difference',
      header: t('assessment.fields.difference'),
      meta: { width: 'auto', minWidth: AMOUNT_COLUMN_MIN_WIDTH },
      cell: ({ row }) => {
        const type = row.original.amount_difference_type;
        const amount = row.original.difference_amount;
        const formattedAmount = formatDifferenceAmount({ amount, type, amountUnit, t });
        return (
          <Text color={getDifferenceColor(type)} className="w-full text-right">
            {formattedAmount}
          </Text>
        );
      },
    },
  ];
};
